import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const DISCLAIMER_TEXT = `IMPORTANT NOTICE — PLEASE READ CAREFULLY

Marome Investments (Pty) Ltd is a technology and information platform.
We are not registered as a Financial Services Provider (FSP) under the
Financial Advisory and Intermediary Services Act, 37 of 2002 (FAIS Act),
and we do not provide financial advice or intermediary services.

Nothing on this platform — including signals, banker leads, trade ideas,
AI analysis, strategy recommendations, or expert chat responses —
constitutes financial advice, a recommendation to buy or sell any financial
instrument, or a solicitation of any investment product.

All analysis and signals are generated by automated systems and artificial
intelligence for informational and educational purposes only.

By using this platform you acknowledge and agree that:

• You are solely responsible for all investment and trading decisions
• Past performance of any signal, strategy or analysis does not guarantee
  future results
• You should consult a licensed financial advisor before making investment
  decisions
• Marome Investments (Pty) Ltd, its directors, employees, and agents are
  not liable for any loss or damage arising from use of information on
  this platform
• You use this platform entirely at your own risk

Trade execution, brokerage fees, and custody of funds are provided by
independent licensed brokers. my investments does not hold client funds.`;

interface DisclaimerModalProps {
  onAcknowledged: () => void;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

const DisclaimerModal: React.FC<DisclaimerModalProps> = ({ onAcknowledged }) => {
  const [hasScrolled, setHasScrolled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const scrollBodyRef = useRef<HTMLDivElement>(null);

  // Block Escape key — modal is non-dismissible
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  // Auto-enable confirm if content fits without scrolling (short viewport or large screen)
  const checkScrollable = useCallback(() => {
    const el = scrollBodyRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      setHasScrolled(true);
    }
  }, []);

  useEffect(() => {
    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, [checkScrollable]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 20) {
      setHasScrolled(true);
    }
  };

  const handleAcknowledge = async () => {
    setLoading(true);
    setApiError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/v1/auth/acknowledge-disclaimer`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to record acknowledgement');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Could not record acknowledgement. Please try again.';
      setApiError(message);
      setLoading(false);
      return;
    }
    onAcknowledged();
  };

  return (
    // Non-dismissible overlay — no onClick handler on backdrop
    <div
      className="fixed inset-0 bg-black/90 z-[9999] flex items-center justify-center p-4"
      data-testid="disclaimer-modal"
    >
      <div className="bg-slate-900 border border-amber-500 rounded-lg max-w-2xl w-full max-h-[85vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="p-6 border-b border-slate-700">
          <h2 className="text-amber-400 font-bold text-lg">
            Platform Regulatory Notice
          </h2>
          <p className="text-slate-400 text-xs mt-1">
            Please read this notice carefully before using my investments.
          </p>
        </div>

        {/* Scrollable body */}
        <div
          ref={scrollBodyRef}
          className="overflow-y-auto p-6 flex-1 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed"
          onScroll={handleScroll}
          data-testid="disclaimer-scroll-body"
        >
          {DISCLAIMER_TEXT}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-slate-700">
          {!hasScrolled && (
            <p className="text-xs text-slate-500 text-center mb-3" data-testid="disclaimer-scroll-hint">
              Please scroll to the bottom to continue
            </p>
          )}
          <button
            disabled={!hasScrolled || loading}
            onClick={handleAcknowledge}
            className="w-full py-3 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="disclaimer-acknowledge-btn"
          >
            {loading ? 'Processing...' : 'I have read and understood this notice'}
          </button>
          {apiError && (
            <p className="text-xs text-red-400 text-center mt-2" data-testid="disclaimer-api-error">
              {apiError}
            </p>
          )}
          <p className="text-xs text-slate-500 text-center mt-3">
            Have questions?{' '}
            <Link
              to="/faq#compliance"
              target="_blank"
              rel="noopener noreferrer"
              className="text-teal-400 hover:text-teal-300 underline"
            >
              Read our FAQ
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default DisclaimerModal;
